import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import apiFiles from 'api/files';
import downloadFile from 'services/files/downloadFile';
import generateFileHeaders from 'services/files/generateFileHeaders';
import IRouteParams from 'interfaces/IRouteParams';
import loadingIcon from 'assets/loading_light_green.svg';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';
import { CSV } from 'services/constants/contentTypes';
import { setRegeneratingDownloads } from 'store/configurationSidebar/actions';

import createJsonForIlogic from './services/createJsonForIlogic';
import getCsvData from '../services/getCsvData';

const Downloads = (): JSX.Element | null => {
  const { configurationId } = useParams<IRouteParams>();
  const { t } = useTranslation(translations.common);
  const dispatch = useDispatch();
  const currentUser = useSelector((state: any) => state.users.current);

  const settings = useSelector(
    (state: any) => state.configurationViewer.settings
  );

  const regeneratingDownloads = useSelector(
    (state: any) => state.configurationSidebar.regeneratingDownloads
  );

  const createWorkItem = async () => {
    dispatch(setRegeneratingDownloads(true));

    await apiFiles.signAndWorkItemInventor.post(
      configurationId,
      createJsonForIlogic(settings.scene)
    );
  };

  const onCsvClick = () => {
    if (currentUser.allowedToReadInventorFiles) {
      downloadFile(
        getCsvData(),
        generateFileHeaders({
          contentType: CSV,
          fileName: 'items.csv',
        })
      );
    } else {
      dispatch(addMessage(t(`${translations.common}:Unauthorized`)));
    }
  };

  const handleDownloadFileError = (error: any) => {
    if (error.response != null && error.response.status === 404) {
      dispatch(addMessage(t(`${translations.common}:File does not exist`)));
      return;
    }

    throw error;
  };

  const onGlassWallsPdfClick = async () => {
    try {
      const response = await apiFiles.downloads.get(
        configurationId,
        'glass-walls-pdf'
      );
      downloadFile(response.data, response.headers);
    } catch (error) {
      handleDownloadFileError(error);
    }
  };

  const onFloorLinesPdfClick = async () => {
    try {
      const response = await apiFiles.downloads.get(
        configurationId,
        'floor-lines-pdf'
      );
      downloadFile(response.data, response.headers);
    } catch (error) {
      handleDownloadFileError(error);
    }
  };

  const onGlassDoorPdfClick = async () => {
    try {
      const response = await apiFiles.downloads.get(
        configurationId,
        'alu-glazy-door-pdf'
      );
      downloadFile(response.data, response.headers);
    } catch (error) {
      handleDownloadFileError(error);
    }
  };

  const onZipClick = async () => {
    if (currentUser.allowedToReadInventorFiles) {
      try {
        const response = await apiFiles.downloads.get(configurationId, 'zip');
        downloadFile(response.data, response.headers);
      } catch (error) {
        handleDownloadFileError(error);
      }
    } else {
      dispatch(addMessage(t(`${translations.common}:Unauthorized`)));
    }
  };

  const onDwfClick = async (type: string) => {
    if (currentUser.allowedToReadInventorFiles) {
      try {
        const response = await apiFiles.downloads.get(configurationId, type);
        downloadFile(response.data, response.headers);
      } catch (error) {
        handleDownloadFileError(error);
      }
    } else {
      dispatch(addMessage(t(`${translations.common}:Unauthorized`)));
    }
  };

  if (regeneratingDownloads) {
    return (
      <div className="spinner-icon-wrapper">
        <div className="spinner-icon">
          <img alt="" src={loadingIcon} />
        </div>
      </div>
    );
  }

  return (
    <>
      <p className="clickable-text" onClick={onGlassWallsPdfClick}>
        {t(`${translations.common}:Download`)}{' '}
        {t(`${translations.common}:Downloads:glass wall`)} PDF
      </p>
      <p className="clickable-text" onClick={onFloorLinesPdfClick}>
        {t(`${translations.common}:Download`)}{' '}
        {t(`${translations.common}:Downloads:floor line`)} PDF
      </p>
      <p className="clickable-text" onClick={onGlassDoorPdfClick}>
        {t(`${translations.common}:Download`)}{' '}
        {t(`${translations.common}:Downloads:alu glazy door pdf`)} PDF
      </p>
      {currentUser.allowedToReadInventorFiles ? (
        <div>
          <p className="clickable-text" onClick={onZipClick}>
            {t(`${translations.common}:Download`)} ZIP
          </p>
          <p className="clickable-text" onClick={onCsvClick}>
            {t(`${translations.common}:Download`)} CSV
          </p>
          <p className="clickable-text" onClick={() => onDwfClick('dwf')}>
            {t(`${translations.common}:Download`)}{' '}
            {t(`${translations.common}:Downloads:3d model`)}
          </p>
          <p className="clickable-text" onClick={() => onDwfClick('no-bw-dwf')}>
            {t(`${translations.common}:Download`)}{' '}
            {t(`${translations.common}:Downloads:3d model glass walls`)}
          </p>
        </div>
      ) : null}
      <PrimaryButton onClick={createWorkItem}>
        {t(`${translations.common}:Downloads:Regenerate files`)}
      </PrimaryButton>
    </>
  );
};

export default Downloads;
